import { cn } from '../../utils/classNames';
import useMultiSelect from '../../hooks/useMultiSelect';
import { useAddCaseFormContext } from '../../contexts/AddCaseFormContext';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import XButton from '../common/XButton';
import TooltipWrapper from './TooltipWrapper';

const containerClasses = 'relative w-full mr-2';

const labelClasses = 'block mb-2 font-medium text-base text-content';

const controlBaseClasses = `xmulti-control
  flex items-center justify-between
  border border-border rounded-md
  bg-white min-h-12.5
  transition-all duration-200 cursor-pointer
  px-2 py-0.5 shadow-sm
  hover:border-border`;

const controlActiveClasses = 'border-primary ring-1 ring-primary/20';

const valueContainerClasses = 'flex flex-1 flex-wrap items-center overflow-hidden p-0.5';

const placeholderClasses = 'text-subtle ml-1';

const selectedValuesClasses = 'flex flex-wrap gap-1 p-0.5';

const tagClasses = `flex items-center
  bg-primary-surface text-primary
  rounded px-2 py-1
  text-sm max-w-full
  transition-colors duration-200
  hover:bg-primary-light`;

const tagTextClasses = 'overflow-hidden text-ellipsis whitespace-nowrap';

const tagRemoveClasses = `ml-1! p-0! text-base!
  leading-none! text-primary!
  hover:text-primary-hover!`;

const indicatorsClasses = 'flex items-center';

const clearIndicatorClasses = `text-subtle cursor-pointer
  p-1 text-base
  hover:text-danger`;

const dropdownIndicatorBaseClasses = `text-subtle px-1
  transition-transform duration-200
  text-[calc(var(--spacing)*2.5)]`;

const menuClasses = `absolute top-full left-0 right-0
  z-10 mt-1 rounded-md
  bg-surface shadow-md
  border border-border-light overflow-hidden`;

const menuListClasses = 'max-h-56 overflow-y-auto py-1';

const noOptionsClasses = 'text-subtle px-3 py-2 text-center';

const optionBaseClasses = `flex items-center
  px-3 py-2 cursor-pointer
  transition-colors duration-200
  hover:bg-surface-muted`;

const optionSelectedClasses = 'bg-primary-surface';

const checkboxWrapperClasses = 'relative flex items-center mr-2';

const hiddenCheckboxClasses = 'absolute opacity-0 cursor-pointer h-0 w-0';

const checkboxBoxBaseClasses = `relative inline-block h-4 w-4
  bg-white border border-border
  rounded transition-all duration-200`;

const checkboxBoxCheckedClasses = 'bg-primary! border-primary!';

const checkmarkClasses = `absolute left-1.5 top-0.5
  w-1.5 h-2.25
  border-r-2 border-b-2 border-white
  rotate-45`;

const optionLabelClasses = 'xmulti-option-label text-sm text-label';

const TeamsSelector = () => {
  
  const teams = useSelector((state) => state.users?.teams) ?? [];

  const {
    getMultiSelectFieldProps
  } = useAddCaseFormContext();

  const {
    selectedValues,
    onChange
  } =  getMultiSelectFieldProps('teamIds');

  const hasSelection = useMemo(()=>{
    return selectedValues?.length;
  }, [selectedValues]);

  const {
    open,
    toggleDropdown,
    ref,
    toggleOption,
    handleRemoveTag,
    handleClearAll
  } = useMultiSelect({
    value: selectedValues,
    onChange,
    options: teams
  });
 

  return (
    <div className={containerClasses} ref={ref}>

      <label className={labelClasses}>
        Teams
        <TooltipWrapper
          title='Teams allow you to share this case with other users'
          showTooltip={true}
        >
          <i className="fa fa-info-circle text-primary m-1!" />
        </TooltipWrapper>
      </label>

      <div
        className={cn(controlBaseClasses, open && controlActiveClasses)}
        onClick={toggleDropdown}
      >
        <div className={valueContainerClasses}>
          {!hasSelection ? (
            <span className={placeholderClasses}>Select Teams</span>
          ) : (
            <div className={selectedValuesClasses}>
              {teams?.filter((option) => selectedValues.includes(option.value)).map((option) => (
                <div key={option.value} className={tagClasses}>
                  <span className={tagTextClasses}>{option.text}</span>
                  <XButton
                    variant="close"
                    className={tagRemoveClasses}
                    onClick={(event) => handleRemoveTag({ value: option.value, event })}
                    aria-label={`Remove ${option.text}`}
                  >
                    ×
                  </XButton>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={indicatorsClasses}>
          {hasSelection && (
            <div
              className={clearIndicatorClasses}
              onClick={handleClearAll}
              title="Clear all"
              role="button"
              aria-label="Clear all selections"
            >
              ⨯
            </div>
          )}
          <div className={cn(dropdownIndicatorBaseClasses, open && 'rotate-180')}>
            ▼
          </div>
        </div>
      </div>

      {open && (
        <div className={menuClasses}>
          <div className={menuListClasses}>
            {teams.length === 0 ? (
              <div className={noOptionsClasses}>No options available. Admins can add teams in the Users menu.</div>
            ) : (
              teams.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <div
                    key={option.value}
                    className={cn(optionBaseClasses, isSelected && optionSelectedClasses)}
                    onClick={() => toggleOption(option.value)}
                  >
                    <div className={checkboxWrapperClasses} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOption(option.value)}
                        id={`option-${option.value}`}
                        className={hiddenCheckboxClasses}
                      />
                      <label
                        htmlFor={`option-${option.value}`}
                        className={cn(checkboxBoxBaseClasses, isSelected && checkboxBoxCheckedClasses)}
                      >
                        {isSelected && <span className={checkmarkClasses} />}
                      </label>
                    </div>
                    <div className={optionLabelClasses}>{option.text}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamsSelector;
